import { PageContainer } from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { CircularProgress, Container, Grid } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ForbiddenError } from "../../../../errors/ForbiddenError";
import { useAuth } from "../../../../hooks/useAuth";
import { ActionsCard } from "../-internal_components/cards/ActionsCard";
import { AdditionalInformationCard } from "../-internal_components/cards/AdditionalInformationCard";
import { AlertCard } from "../-internal_components/cards/AlertCard";
import { DecisionCards } from "../-internal_components/cards/DecisionCard";
import { DetailsCard } from "../-internal_components/cards/DetailsCard";
import { ProcessCard } from "../-internal_components/cards/ProcessCard";
import { RelatedRequestsCard } from "../-internal_components/cards/RelatedRequestsCard";
import { StatusCard } from "../-internal_components/cards/StatusCard";
import { deliveryDateChangeRequestsDetailQueryOptions } from "../-queries";

const ShowDeliveryDateChangeRequest: React.FC = () => {
  const { deliveryDateChangeRequestId } = Route.useParams();

  const { user } = useAuth();
  const { t } = useTranslation();

  const { data: deliveryDateChangeRequest, isLoading } = useQuery(
    deliveryDateChangeRequestsDetailQueryOptions(deliveryDateChangeRequestId),
  );

  const status = deliveryDateChangeRequest?.status;

  if (isLoading || !deliveryDateChangeRequest) {
    return (
      <Container
        sx={{
          alignItems: "center",
          display: "flex",
          height: "100vh",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }
  return (
    <PageContainer
      showBackButton
      subtitle={t(
        "deliveryDateChangeRequests.titles.deliveryDateRequestShowSubtitle",
      )}
      title={t("deliveryDateChangeRequests.titles.deliveryDateRequestShow")}
    >
      <AlertCard
        isPendingAlertVisible={
          user?.roles_names.includes(
            "Delivery Date Change Request - Approve",
          ) || false
        }
        status={status}
      />
      <Grid
        container
        spacing={3}
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 3,
          width: "100%",
        }}
      >
        <Grid
          size={8}
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          <DetailsCard deliveryDateChangeRequest={deliveryDateChangeRequest} />
          <DecisionCards
            deliveryDateChangeRequest={deliveryDateChangeRequest}
            isDecisionButtonVisible={
              !!user?.roles_names.includes(
                "Delivery Date Change Request - Approve",
              )
            }
          />
          <ProcessCard deliveryDateChangeRequest={deliveryDateChangeRequest} />
          <RelatedRequestsCard
            deliveryDateChangeRequest={deliveryDateChangeRequest}
          />
        </Grid>
        <Grid
          size={4}
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          <StatusCard deliveryDateChangeRequest={deliveryDateChangeRequest} />
          <AdditionalInformationCard
            deliveryDateChangeRequest={deliveryDateChangeRequest}
          />
          {!!status && (
            <ActionsCard id={deliveryDateChangeRequest.id} status={status} />
          )}
        </Grid>
      </Grid>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/delivery_date_change_requests/$deliveryDateChangeRequestId/",
)({
  beforeLoad({ context }) {
    if (
      context.auth.user?.roles_names.includes(
        "Delivery Date Change Request - View",
      )
    ) {
      return;
    }

    throw new ForbiddenError();
  },
  component: ShowDeliveryDateChangeRequest,
});
